import {RenditionPolicy, Workspace} from '../../../types';
import {Chip, InputLabel, ListItemText, TextField} from '@mui/material';
import {FormRow} from '@alchemy/react-form';
import DefinitionManager from './DefinitionManager/DefinitionManager.tsx';
import {useTranslation} from 'react-i18next';
import {FormFieldErrors} from '@alchemy/react-form';
import {
    deleteRenditionPolicy,
    getRenditionPolicies,
    postRenditionPolicy,
    putRenditionPolicy,
} from '../../../api/rendition';
import {CheckboxWidget} from '@alchemy/react-form';
import RenditionPolicyPermissions from './RenditionPolicyPermissions';
import {DataTabProps} from '../Tabbed/TabbedDialog.tsx';
import {
    DefinitionItemFormProps,
    DefinitionItemProps,
} from './DefinitionManager/managerTypes.ts';
import {EntityName} from '../../../api/types.ts';
import {createIriFromId} from '@alchemy/api';

function Item({
    data,
    usedFormSubmit: {
        submitting,
        register,
        control,
        watch,
        formState: {errors},
    },
}: DefinitionItemFormProps<RenditionPolicy>) {
    const {t} = useTranslation();

    const isPublic = watch('public');
    const isEditable = watch('editable');

    return (
        <>
            <FormRow>
                <TextField
                    label={t('form.rendition_policy.name.label', 'Name')}
                    {...register('name')}
                    disabled={submitting}
                />
                <FormFieldErrors field={'name'} errors={errors} />
            </FormRow>
            <FormRow>
                <CheckboxWidget
                    label={t('form.rendition_policy.public.label', 'Public')}
                    control={control}
                    name={'public'}
                    disabled={submitting}
                />
                <FormFieldErrors field={'public'} errors={errors} />
            </FormRow>
            <FormRow>
                <CheckboxWidget
                    label={t(
                        'form.rendition_policy.editable.label',
                        'Editable'
                    )}
                    control={control}
                    name={'editable'}
                    disabled={!isPublic || submitting}
                />
                <FormFieldErrors field={'editable'} errors={errors} />
            </FormRow>
            {data.id && !isEditable && (
                <FormRow>
                    <InputLabel>
                        {t('form.permissions.label', 'Permissions')}
                    </InputLabel>
                    <RenditionPolicyPermissions
                        isPublic={isPublic}
                        policyId={data.id}
                        workspaceId={(data.workspace as Workspace).id}
                    />
                </FormRow>
            )}
        </>
    );
}

function ListItem({data}: DefinitionItemProps<RenditionPolicy>) {
    const {t} = useTranslation();

    const publicLabel = data.public
        ? t('chip.public', 'Public')
        : t('chip.private', 'Private');
    const editableLabel = data.public
        ? data.editable
            ? t('chip.editable', 'Editable')
            : t('chip.read_only', 'Read only')
        : undefined;

    return (
        <ListItemText
            primary={data.name}
            slotProps={{
                secondary: {
                    component: 'div',
                },
            }}
            secondary={
                <>
                    <Chip
                        color={data.public ? 'success' : 'error'}
                        label={publicLabel}
                        size={'small'}
                    />{' '}
                    {editableLabel ? (
                        <Chip
                            color={data.editable ? 'success' : 'error'}
                            label={editableLabel}
                            size={'small'}
                        />
                    ) : null}
                </>
            }
        />
    );
}

function createNewItem(): Partial<RenditionPolicy> {
    return {
        name: '',
        public: true,
        editable: true,
    };
}

type Props = DataTabProps<Workspace>;

export default function RenditionPolicyManager({
    data: workspace,
    minHeight,
    onClose,
}: Props) {
    const {t} = useTranslation();

    const handleSave = async (data: RenditionPolicy) => {
        if (data.id) {
            const postData = {...data} as Partial<RenditionPolicy>;
            delete postData.workspace;

            return await putRenditionPolicy(data.id, postData);
        } else {
            return await postRenditionPolicy({
                ...data,
                workspace: createIriFromId(EntityName.Workspace, workspace.id),
            });
        }
    };

    return (
        <DefinitionManager
            itemComponent={Item}
            listComponent={ListItem}
            load={({nextUrl}) =>
                getRenditionPolicies({
                    workspaceId: workspace.id,
                    nextUrl,
                })
            }
            workspace={workspace}
            minHeight={minHeight}
            onClose={onClose}
            createNewItem={createNewItem}
            newLabel={t('rendition_policy.new.label', 'New Policy')}
            handleSave={handleSave}
            handleDelete={deleteRenditionPolicy}
        />
    );
}
